import { WSAlert } from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";
import { formatNumber } from "accounting";
import { useState } from "react";

import { selectorIsImportBatchFullyImported } from "../../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../../selectors/selectorIsImportBatchPartiallyImported";

type Props = {
  batch: Batch;
  /** `BatchSummary.progress.total`. */
  itemCount?: number;
  /** `BatchSummary.progress.completed`. */
  completedCount?: number;
  /** `BatchSummary.progress.failed`. */
  failedCount?: number;
};

/** Outcome banner shown once the import has run. */
export const ImportBatchStatusAlert: React.FC<Props> = ({
  batch,
  itemCount,
  completedCount,
  failedCount
}) => {
  const [showCompleteAlert, setShowCompleteAlert] = useState(true);

  const formattedTotal = formatNumber(itemCount || 0);
  const formattedProcessed = formatNumber(completedCount || 0);
  const formattedFailed = formatNumber(failedCount || 0);

  if (selectorIsImportBatchFullyImported({ batch, failedCount })) {
    return showCompleteAlert ? (
      <WSAlert
        theme="success"
        icon="check-circle"
        title="Import complete"
        onDismiss={() => setShowCompleteAlert(false)}
      >
        All items were imported from the spreadsheet. To add a name and notes to
        the import batch for your own reference click the “Edit name” button.
      </WSAlert>
    ) : null;
  }

  if (selectorIsImportBatchPartiallyImported({ batch, failedCount })) {
    return (
      <WSAlert theme="warning" icon="alert" title="Partially imported">
        {formattedProcessed} of {formattedTotal} items were imported from the
        spreadsheet and {formattedFailed} of {formattedTotal} failed. A
        spreadsheet with the errors is available for download. You can resolve
        the errors and upload in a new import batch. To add a name and notes to
        the import batch for your own reference click “Edit name” below.
      </WSAlert>
    );
  }

  if (batch.status === BatchStatus.FAILED) {
    return (
      <WSAlert theme="error" icon="alert-circle" title="Import failed">
        {formattedFailed} of {formattedTotal} items failed to import from the
        spreadsheet. A spreadsheet with the errors is available for download.
        You can resolve the errors and upload in a new import batch. To add a
        name and notes to the import batch for your own reference click “Edit
        name” below.
      </WSAlert>
    );
  }

  return null;
};
